import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, TrendingUp, Brain, Cloud, Target, Code } from 'lucide-react';
import { Breadcrumb } from '../components/Breadcrumb';
import { useBanner } from '../contexts/BannerContext';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  readTime: string;
  category: string;
  tags: string[];
  image?: string;
  featured?: boolean;
}

export const BlogPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const { isBannerVisible, bannerHeight } = useBanner();
  
  // Mock blog posts data - in a real app this would come from a CMS or API
  const blogPosts: BlogPost[] = [
    {
      id: 'ai-marketing-trends-2025',
      title: 'AI Marketing Trends That Will Dominate 2025',
      excerpt: 'Discover the latest AI-powered marketing strategies that successful businesses are implementing to stay ahead of the competition.',
      content: '',
      date: '2025-01-10',
      readTime: '8 min read',
      category: 'AI & Marketing',
      tags: ['AI', 'Marketing', 'Trends', 'Digital Marketing'],
      image: 'https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/d5b8a474-1e18-48a9-ab86-feddbc82b300/public',
      featured: true
    },
    {
      id: 'cloud-migration-guide',
      title: 'Complete Guide to Cloud Migration for Small Businesses',
      excerpt: 'Step-by-step guide to migrating your business to the cloud safely and efficiently, with minimal downtime and maximum benefits.',
      content: '',
      date: '2025-01-08',
      readTime: '12 min read',
      category: 'Cloud Computing',
      tags: ['Cloud', 'Migration', 'Small Business', 'Infrastructure'],
      image: 'https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/ed138377-0839-459d-deb1-d903e6219700/public'
    },
    {
      id: 'automation-roi-calculator',
      title: 'How to Calculate ROI for Business Process Automation',
      excerpt: 'Learn how to measure the return on investment for automation projects and make data-driven decisions about your digital transformation.',
      content: '',
      date: '2025-01-05',
      readTime: '6 min read',
      category: 'Business Automation',
      tags: ['Automation', 'ROI', 'Business Process', 'Analytics'],
      image: 'https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/3ed87b95-6e04-44fb-e596-5d9eaa383100/public'
    },
    {
      id: 'cybersecurity-best-practices',
      title: 'Essential Cybersecurity Practices for Modern Businesses',
      excerpt: 'Protect your business from cyber threats with these proven security strategies and implementation guidelines.',
      content: '',
      date: '2025-01-03',
      readTime: '10 min read',
      category: 'Security',
      tags: ['Cybersecurity', 'Security', 'Best Practices', 'Business Protection'],
      image: 'https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/bba12764-ad29-4840-4092-e21444ac3800/public'
    },
    {
      id: 'iot-smart-offices',
      title: 'Building Smart Offices with IoT: A Practical Guide',
      excerpt: 'Transform your workplace with IoT solutions that improve efficiency, reduce costs, and enhance employee experience.',
      content: '',
      date: '2025-01-01',
      readTime: '9 min read',
      category: 'IoT',
      tags: ['IoT', 'Smart Office', 'Workplace Technology', 'Efficiency'],
      image: 'https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/6bd2a33d-6d5b-4655-132f-06d8dfbcee00/public'
    },
    {
      id: 'machine-learning-business-insights',
      title: 'Unlocking Business Insights with Machine Learning',
      excerpt: 'Discover how machine learning can reveal hidden patterns in your business data and drive strategic decision-making.',
      content: '',
      date: '2024-12-28',
      readTime: '11 min read',
      category: 'Machine Learning',
      tags: ['Machine Learning', 'Business Intelligence', 'Data Analytics', 'Insights'],
      image: 'https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/20aab9cf-205f-46d0-d072-baebda760300/public'
    }
  ];

  const categories = [
    'All',
    'AI & Marketing',
    'Cloud Computing',
    'Business Automation',
    'Security',
    'IoT',
    'Machine Learning'
  ];

  const categoryIcons: Record<string, React.ReactNode> = {
    'AI & Marketing': <Target className="w-4 h-4" />,
    'Cloud Computing': <Cloud className="w-4 h-4" />,
    'Business Automation': <TrendingUp className="w-4 h-4" />,
    'Security': <Brain className="w-4 h-4" />,
    'IoT': <Code className="w-4 h-4" />,
    'Machine Learning': <Brain className="w-4 h-4" />
  };

  const featuredPost = blogPosts.find(post => post.featured);
  const allRegularPosts = blogPosts.filter(post => !post.featured);
  
  // Filter posts based on selected category
  const filteredPosts = selectedCategory === 'All' 
    ? allRegularPosts 
    : allRegularPosts.filter(post => post.category === selectedCategory);

  return (
    <>
      <Breadcrumb />
      <main className="pb-20">
        {/* Hero Section */}
        <section 
          className="px-4 sm:px-6 lg:px-8 mb-20"
          style={{ 
            paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
          }}
        >
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
              Insights & Resources
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              Stay ahead with expert insights on AI, technology trends, and business innovation
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Explore our collection of in-depth articles, guides, and industry analysis to help you make informed decisions for your business transformation.
            </p>
          </div>
        </section>

        {/* Featured Post */}
        {featuredPost && (
          <section className="px-4 sm:px-6 lg:px-8 mb-20">
            <div className="max-w-7xl mx-auto">
              <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-0">
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="flex items-center space-x-2 mb-4">
                      <span className="px-3 py-1 bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-full text-sm font-medium">
                        Featured
                      </span>
                      <span className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm">
                        {featuredPost.category}
                      </span>
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                      {featuredPost.title}
                    </h2>
                    <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{featuredPost.readTime}</span>
                        </div>
                      </div>
                      <Link
                        to={`/blog/${featuredPost.id}`}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                      >
                        Read More
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </div>
                  </div>
                  <div className="relative h-64 lg:h-full">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-blue-700/20 overflow-hidden rounded-r-3xl">
                      <img 
                        src="https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/d5b8a474-1e18-48a9-ab86-feddbc82b300/public"
                        alt="AI Marketing Trends"
                        className="w-full h-full object-cover opacity-80"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Categories Filter */}
        <section className="px-4 sm:px-6 lg:px-8 mb-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full border transition-all duration-300 flex items-center space-x-2 ${
                    selectedCategory === category
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'border-gray-300 dark:border-white/30 text-gray-700 dark:text-white hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-600'
                  }`}
                >
                  {categoryIcons[category] && categoryIcons[category]}
                  <span>{category}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="px-4 sm:px-6 lg:px-8 mb-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300 group"
                >
                  <div className="relative h-48 bg-gradient-to-br from-blue-600/20 to-blue-700/20 flex items-center justify-center overflow-hidden rounded-t-2xl">
                    {post.category === 'Cloud Computing' ? (
                      <img 
                        src="https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/ed138377-0839-459d-deb1-d903e6219700/public"
                        alt="Cloud Computing"
                        className="w-full h-full object-cover opacity-80"
                      />
                    ) : post.category === 'Business Automation' ? (
                      <img 
                        src="https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/3ed87b95-6e04-44fb-e596-5d9eaa383100/public"
                        alt="Business Automation"
                        className="w-full h-full object-cover opacity-80"
                      />
                    ) : post.category === 'Security' ? (
                      <img 
                        src="https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/bba12764-ad29-4840-4092-e21444ac3800/public"
                        alt="Security"
                        className="w-full h-full object-cover opacity-80"
                      />
                    ) : post.category === 'IoT' ? (
                      <img 
                        src="https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/6bd2a33d-6d5b-4655-132f-06d8dfbcee00/public"
                        alt="IoT"
                        className="w-full h-full object-cover opacity-80"
                      />
                    ) : post.category === 'Machine Learning' ? (
                      <img 
                        src="https://imagedelivery.net/mp-DXtqFu5Vq5mNNPemvvQ/20aab9cf-205f-46d0-d072-baebda760300/public"
                        alt="Machine Learning"
                        className="w-full h-full object-cover opacity-80"
                      />
                    ) : categoryIcons[post.category] ? (
                      <div className="text-blue-600 dark:text-blue-400 opacity-60">
                        {React.cloneElement(categoryIcons[post.category] as React.ReactElement, { className: "w-16 h-16" })}
                      </div>
                    ) : (
                      <Brain className="w-16 h-16 text-blue-600 dark:text-blue-400 opacity-60" />
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded text-xs">
                        {post.category}
                      </span>
                      <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {post.title}
                    </h3>
                    
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-4 leading-relaxed">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>{new Date(post.date).toLocaleDateString()}</span>
                      </div>
                      <Link
                        to={`/blog/${post.id}`}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm inline-flex items-center group"
                      >
                        Read More
                        <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
};